import { AppContext } from '../../context';
import { DateTime } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MultiDayOption, SingleDayOption } from '../../types/index';
import { faWindowRestore } from '@fortawesome/pro-solid-svg-icons';

import {
    getDayAbbreviated,
    getDayMonthDate,
    getDayOfMonth,
    getMonthAbbreviated,
    getTimeSimple,
} from '../../utils/date-utils';
import { isMobile } from 'react-device-detect';
import MultiDayDivider from './MultiDayDivider';
import React, { ReactElement, useContext, useState } from 'react';

import './InterviewOptionPicker.scss';
import SpinnerLoading from '../SpinnerLoading';

interface InterviewOptionPickerProps {
    companyColor: string;
    textColor: string;
    singleDayInterviewOptions?: SingleDayOption[];
    multiDayInterviewOptions?: MultiDayOption[];
    selectedTimeIds?: (string | undefined)[];
    setSelectedTimeIds: (selectedTimeId: string) => void;
    setSelectedTimeCategory?: (day: string, timeCategory: string) => void;
    date?: string;
    numberOfDays?: number;
    timezone?: string;
    displaySuggestTimesSlots?: (date: string) => void;
    allowGuestToSuggestTimes?: boolean;
    allowToSuggestTimesFeatureFlag?: boolean;
    isAlgoLoading?: boolean;
}

const buildHeaders = (
    translate: (text: string, dynamicValueObject?: Record<string, unknown>) => string,
    singleDayInterviewOptions?: SingleDayOption[],
    multiDayInterviewOptions?: MultiDayOption[],
    date?: string,
    numberOfDays?: number,
) => {
    let header = translate('general:session', { count: 0 });
    let overHeader = translate('general:interviews', { count: 0 });

    if (date && singleDayInterviewOptions && singleDayInterviewOptions.length) {
        const parsedDate = new Date(date);
        const dateForHeader = DateTime.fromJSDate(parsedDate);
        header = getDayMonthDate(dateForHeader);
        overHeader = translate('pickDays:interviewsStartingOn', { count: singleDayInterviewOptions.length });
    }

    if (date && !singleDayInterviewOptions?.length) {
        const parsedDate = new Date(date);
        const dateForHeader = DateTime.fromJSDate(parsedDate);
        header = getDayMonthDate(dateForHeader);
        overHeader = translate('pickDays:interviewsStartingOn', { count: 0 });
    }

    if (numberOfDays && multiDayInterviewOptions && multiDayInterviewOptions.length) {
        header = translate('general:session', { count: numberOfDays });
        overHeader = translate('pickDays:interviewSpanningOver', { count: multiDayInterviewOptions.length });
    }

    return { header, overHeader };
};

const InterviewOptionsPicker = ({
    companyColor,
    textColor,
    singleDayInterviewOptions,
    multiDayInterviewOptions,
    selectedTimeIds,
    setSelectedTimeIds,
    setSelectedTimeCategory = () => [],
    date,
    numberOfDays,
    timezone,
    displaySuggestTimesSlots,
    allowGuestToSuggestTimes,
    allowToSuggestTimesFeatureFlag,
    isAlgoLoading,
}: InterviewOptionPickerProps) => {
    const { translate } = useContext(AppContext);
    const [clicked, setClicked] = useState<string[]>([]);
    const timeCategoriesSet: string[] = [];
    const idForSuggestTimeButton = date ? `${DateTime.fromFormat(date, 'M/d/y').toMillis() / 100000}` : '';
    const { header, overHeader } = buildHeaders(
        translate,
        singleDayInterviewOptions,
        multiDayInterviewOptions,
        date,
        numberOfDays,
    );
    const isSuggestionMessageVisible = !singleDayInterviewOptions?.length && !multiDayInterviewOptions?.length;
    return (
        <div className="optionsPickerContainer">
            <div
                className="optionsPickerTitle"
                style={{
                    backgroundColor: companyColor,
                    color: isMobile ? '#FFFFFF' : textColor,
                }}
            >
                <div className="optionsPickerSubtitle">{overHeader}</div>
                <div className="optionsPickerDate">{header}</div>
            </div>
            <div className="optionPickerContentContainer">
                <div className="optionPickerContent">
                    {multiDayInterviewOptions?.map((multiDayOption, multiDayIndex) => {
                        return (
                            <React.Fragment key={`multi-day-option-${multiDayOption.hash}-${multiDayIndex}`}>
                                {multiDayIndex === 0 ? (
                                    <div className="optionsPickerDateGroup">
                                        <FontAwesomeIcon
                                            className="optionsPickerDateGroupIcon"
                                            icon={faWindowRestore}
                                        />
                                        {translate('pickDays:overMultipleDays')}
                                        <div className="optionsPickerDateGroupLine" />
                                    </div>
                                ) : null}
                                <div
                                    onClick={() => setSelectedTimeIds(multiDayOption.hash)}
                                    className={
                                        selectedTimeIds?.includes(multiDayOption.hash)
                                            ? 'multiDayDateOption selected'
                                            : 'multiDayDateOption'
                                    }
                                >
                                    {multiDayOption.duration.map((duration, index) => {
                                        const startTimeAsDate: DateTime = DateTime.fromISO(duration.startTime, {
                                            zone: timezone,
                                        });
                                        const endTimeAsDate: DateTime = DateTime.fromISO(duration.endTime, {
                                            zone: timezone,
                                        });

                                        return (
                                            <React.Fragment
                                                key={`time-slot-in-multi-day-option-${multiDayOption.hash}-${index}`}
                                            >
                                                <div className="multiDaySingleDateOption">
                                                    <div className="multiDaySingleDatePrefix">
                                                        <div className="multiDaySingleDatePrefixContent">
                                                            <div className="multiDaySingleDatePrefixDay">
                                                                {getDayAbbreviated(startTimeAsDate).toUpperCase()}
                                                            </div>
                                                            <div className="multiDaySingleDatePrefixDayNum">
                                                                {getDayOfMonth(startTimeAsDate)}
                                                            </div>
                                                            <div className="multiDaySingleDatePrefixMonth">
                                                                {getMonthAbbreviated(startTimeAsDate).toUpperCase()}
                                                            </div>
                                                        </div>
                                                        {index !== multiDayOption.duration.length - 1 ? (
                                                            <MultiDayDivider
                                                                isSelected={
                                                                    selectedTimeIds
                                                                        ? selectedTimeIds?.includes(multiDayOption.hash)
                                                                        : false
                                                                }
                                                                selectedColor={textColor}
                                                            />
                                                        ) : null}
                                                    </div>
                                                    <div className="multiDaySingleDateOptionContent">
                                                        <div className="singleDayDateOptionInMultiDayPrefix">
                                                            <div className="singleDayDateOptionInMultiDayPrefixDayNum">
                                                                {translate('general:day', { count: index + 1 })}
                                                            </div>
                                                            <div className="singleDayDateOptionInMultiDayPrefixDate">
                                                                {getDayMonthDate(startTimeAsDate)}
                                                            </div>
                                                        </div>
                                                        <div className="singleDayDateOptionInMultiDay">
                                                            <div className="dayOptionTimeRage">
                                                                {`${getTimeSimple(startTimeAsDate)} - ${getTimeSimple(
                                                                    endTimeAsDate,
                                                                )}`}
                                                            </div>
                                                            <div className="singleDayOptionDuration">
                                                                {duration.durationTime}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            </React.Fragment>
                        );
                    })}
                    {singleDayInterviewOptions?.map((singleDayOption, singleDayIndex) => {
                        const startTimeAsDate: DateTime = DateTime.fromISO(singleDayOption.duration.startTime, {
                            zone: timezone,
                        });
                        const endTimeAsDate: DateTime = DateTime.fromISO(singleDayOption.duration.endTime, {
                            zone: timezone,
                        });

                        let timeCategoryComponent: string | ReactElement = '';
                        let singleDayClasses = selectedTimeIds?.includes(singleDayOption.hash)
                            ? 'singleDayDateOption selected'
                            : 'singleDayDateOption';

                        if (singleDayOption?.isSuggested) {
                            singleDayClasses += ' isSuggested';
                        }

                        if (!timeCategoriesSet.includes(singleDayOption.timeCategory.label)) {
                            timeCategoriesSet.push(singleDayOption.timeCategory.label);
                            timeCategoryComponent = (
                                <div className="optionsPickerDateGroup">
                                    <div className="grayLabel">
                                        <FontAwesomeIcon
                                            className="optionsPickerDateGroupIcon"
                                            icon={singleDayOption.timeCategory.icon}
                                        />
                                        {translate(`pickDays:${singleDayOption.timeCategory.key}`)}
                                    </div>

                                    <div className="optionsPickerDateGroupLine" />
                                    <div className="optionsPickerDateGroupSelectAll">
                                        <button
                                            className="grayLabel selectAll"
                                            onClick={() => {
                                                setSelectedTimeCategory(date ?? '', singleDayOption.timeCategory.label);
                                            }}
                                        >
                                            {translate('pickDays:selectAll')}
                                        </button>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            singleDayOption.hash && (
                                <React.Fragment key={`single-day-option-${singleDayOption.hash}-${singleDayIndex}`}>
                                    {timeCategoryComponent}
                                    <div
                                        onClick={() => setSelectedTimeIds(singleDayOption.hash)}
                                        className={singleDayClasses}
                                        style={{ borderColor: singleDayOption?.isSuggested ? 'gray' : companyColor }}
                                    >
                                        <div className="dayOptionTimeRage">
                                            {getTimeSimple(startTimeAsDate)} - {getTimeSimple(endTimeAsDate)}
                                        </div>
                                        <div className="singleDayOptionDuration">
                                            {singleDayOption.duration.durationTime}
                                        </div>
                                    </div>
                                </React.Fragment>
                            )
                        );
                    })}
                </div>
                {isSuggestionMessageVisible && isAlgoLoading ? (
                    <div className="optionPickerSuggestTimes noOptions">
                        <p>{translate('pickDays:notFindingAGoodTime')}</p>
                    </div>
                ) : null}
                {isSuggestionMessageVisible && !isAlgoLoading ? (
                    <>
                        <div className="optionPickerSuggestTimes spinner">
                            <SpinnerLoading
                                loading={!isAlgoLoading}
                                message={'Loading'}
                                isTopSpinner={false}
                            ></SpinnerLoading>
                        </div>
                        <div className="optionPickerSuggestTimes loadingMessage">
                            <p>{translate('pickDays:searchingForAGoodTime')}</p>
                        </div>
                    </>
                ) : null}
                {allowToSuggestTimesFeatureFlag &&
                    allowGuestToSuggestTimes &&
                    !clicked.includes(idForSuggestTimeButton) && (
                        <div className="optionPickerSuggestTimes" id={idForSuggestTimeButton}>
                            <button
                                className="suggestTimeBtn"
                                onClick={() => {
                                    if (date) {
                                        setClicked([...clicked, idForSuggestTimeButton]);
                                        displaySuggestTimesSlots?.(date);
                                    }
                                }}
                            >
                                {translate('pickDays:suggestTimes')}
                            </button>
                        </div>
                    )}
            </div>
        </div>
    );
};

export default InterviewOptionsPicker;
